"use client";

import { useRef } from "react";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { countTo, reduceMotion } from "@/lib/motion";
import { AnimatedCard } from "@/components/motion/AnimatedCard";
import { PROBLEM } from "@/content/quikit";
import { SectionIntro } from "./SectionIntro";

/** The problem — three research stats arriving on the scroll-linked diagonal cascade. */
export function Problem() {
  const grid = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const el = grid.current;
      if (!el || reduceMotion()) return;
      gsap.utils.toArray<HTMLElement>("[data-stat]", el).forEach((card, i) => {
        const s = PROBLEM.stats[i];
        gsap
          .timeline({ scrollTrigger: { trigger: card, start: "top 78%", toggleActions: PLAY_ONCE }, delay: 0.15 + i * 0.1 })
          .add(countTo(card.querySelector("[data-stat-value]"), s.value, { suffix: s.suffix, duration: 1.8 }))
          .fromTo(card.querySelector("[data-stat-bar]"), { scaleX: 0 }, { scaleX: 1, duration: 1.4, ease: "silk" }, 0.1);
      });
    },
    { scope: grid },
  );

  return (
    <section id="problem" aria-labelledby="problem-title" className="pt-[clamp(110px,12vw,180px)]">
      <div className="wrap">
        <SectionIntro eyebrow={PROBLEM.eyebrow} title={PROBLEM.title} titleId="problem-title" text={PROBLEM.text} textClassName="max-w-[52ch]" />
        <div ref={grid} className="mt-12 grid gap-3 md:mt-14 md:grid-cols-3 md:gap-4">
          {PROBLEM.stats.map((s, i) => (
            <AnimatedCard
              key={s.source}
              index={i}
              scrub
              cascade={72}
              y={96}
              scale={1}
              className="flex flex-col rounded-[24px] bg-panel p-6 md:min-h-[300px] md:p-7"
            >
              <div data-stat="" className="flex h-full flex-col">
                <p data-stat-value="" className="text-[clamp(3rem,5vw,4.4rem)] font-[450] leading-none tabular-nums tracking-[-0.05em]">
                  {s.value}
                  {s.suffix}
                </p>
                <div className="mt-6 h-1 overflow-hidden rounded-full bg-white">
                  <div data-stat-bar="" className="h-full origin-left rounded-full bg-[#5fd9cc]" style={{ width: `${Math.min(100, s.value * (s.suffix === "%" ? 1 : 18))}%` }} />
                </div>
                <p className="mt-6 text-[14px] leading-[1.55] text-ink/80">{s.text}</p>
                <p className="mt-auto pt-6 text-[11.5px] text-faint">{s.source}</p>
              </div>
            </AnimatedCard>
          ))}
        </div>
      </div>
    </section>
  );
}
